/**
 * Operator-editable display labels.
 *
 * Two label classes live here:
 *
 *   - **Service display name** lives on `Service.display_name` (set
 *     via ``PATCH /services/{id}``). The operator can rename a row
 *     without touching the immutable slug `service.name`.
 *   - **Pool display name** lives on `Pool.display_name` (set via
 *     ``PATCH /pools/{id}``). Falls back to the slug
 *     ``Pool.sky_service_name``, which can also be rewritten by the
 *     controller when the operator first commits a real shape +
 *     placement on a draft pool (see
 *     `controller/api/routers/pools.py::_maybe_promote_slug`).
 *     Resolution is a pure function over the Pool row itself — no
 *     separate cache to keep in sync — so the rename pencil reflects
 *     the latest value as soon as TanStack Query invalidates.
 *
 * GPU-tier overrides used to live here too, keyed by accelerator
 * slug, when the tree had a virtual "GPU group" row above each Pool.
 * That row is gone (the Pool is itself a same-shape group), so the
 * tier label store has been removed; resource shape rendering now
 * happens directly on the Pool row via `humanShapeLabel`.
 *
 * Reads piggy-back on the standard `["services"]` query the page
 * already runs (no extra round-trip for service labels). Pool labels
 * are read directly off whichever Pool row the caller already holds.
 * Writes are exposed as mutation hooks that PATCH the underlying
 * resource and invalidate the relevant query.
 */
import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { Pool, Service } from "@/api/types";

export interface LabelsDoc {
  /** serviceId -> override (only entries with a non-empty override). */
  services: Record<number, string>;
}

const EMPTY_DOC: LabelsDoc = { services: {} };

const SERVICES_KEY = ["services"] as const;

/**
 * Combined doc consumed by `Workloads.tsx` to build the tree. Falls
 * back to an empty map while the query is loading so the tree still
 * renders with slug-based service names.
 *
 * Subscribes to the same `["services"]` query the page already runs,
 * so a teammate's PATCH that lands through revalidation flows here
 * without an extra fetch.
 */
export function useLabels(): LabelsDoc {
  const services = useQuery({
    queryKey: SERVICES_KEY,
    queryFn: () => api.get<Service[]>("/services"),
    staleTime: 10_000,
  });

  return useMemo(() => {
    const servicesMap: Record<number, string> = {};
    for (const s of services.data ?? []) {
      const v = s.display_name?.trim();
      if (v) servicesMap[s.id] = v;
    }
    return { services: servicesMap };
  }, [services.data]);
}

/** Resolve service display name with override fallback. */
export function resolveServiceLabel(
  doc: LabelsDoc,
  serviceId: number,
  fallback: string,
): string {
  const o = doc.services[serviceId];
  return o && o.trim() ? o : fallback;
}

/**
 * Resolve a pool's display name. Pure function over the Pool row —
 * the caller already holds the freshest copy from
 * `useAllPools` / a single-pool fetch, so we don't need a parallel
 * cache. NULL/empty `display_name` falls back to the slug.
 */
export function resolvePoolLabel(pool: Pool): string {
  const v = pool.display_name?.trim();
  return v && v.length > 0 ? v : pool.sky_service_name;
}

/**
 * Hook variant: subscribe to the labels doc and resolve one service
 * row's display name. Re-renders when the underlying services list
 * (and therefore display_name) changes.
 */
export function useServiceLabel(serviceId: number, fallback: string): string {
  const doc = useLabels();
  return resolveServiceLabel(doc, serviceId, fallback);
}

/**
 * Mutation: set or clear a service's display name. Pass an empty
 * string to clear the override (= fall back to the slug).
 *
 * The returned function is stable across renders so it can be passed
 * directly into `onClick` etc.
 */
export function useSetServiceLabel() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, label }: { id: number; label: string }) => {
      const trimmed = label.trim();
      await api.patch<Service>(`/services/${id}`, {
        display_name: trimmed || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SERVICES_KEY });
    },
  });
  return useCallback(
    async (id: number, label: string) =>
      mutation.mutateAsync({ id, label }),
    [mutation],
  );
}

/**
 * Mutation: set or clear a pool's display name. Same empty-string =
 * clear semantics as `useSetServiceLabel`. Invalidates every
 * ``["pools",...]`` query (the page caches per-service-id sets via
 * `useAllPools`, plus single-pool reads on the detail tabs) so the
 * tree label and SubNav title refresh immediately.
 */
export function useSetPoolLabel() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async ({ id, label }: { id: number; label: string }) => {
      const trimmed = label.trim();
      await api.patch<Pool>(`/pools/${id}`, {
        display_name: trimmed || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pools"] });
    },
  });
  return useCallback(
    async (id: number, label: string) => mutation.mutateAsync({ id, label }),
    [mutation],
  );
}

// `EMPTY_DOC` is exported as a stable reference for callers that want
// to render before the queries hydrate (e.g. SSR, tests).
export { EMPTY_DOC };
